import clsx from 'clsx';
import { ViewDocumentModal } from '@/app/(main)/clients/components/ViewDocumentModal';
import { Tooltip } from '@/app/components/Tooltip';
import { TooltipWrapper } from '@/app/components/Tooltip/components/TooltipWrapper';
import { useModalState } from '@/hooks/useModalState';
import type { ClientDocumentWithPassportData, PassportDataWithName } from '@/types/clientsApiTypes';
import { cn } from '@/utils/сn';
import { ConfirmationDeleteFileModal } from '../../../ConfirmationDeleteFileModal';
import { PreviewDocument } from '../../../PreviewDocument';
import EditIcon from '/public/icons/pencil-square.svg';
import TrashIcon from '/public/icons/trash.svg';

interface TableRowProps {
  document: ClientDocumentWithPassportData;
  fieldPrefix: string;
  tableRowClassName?: string;
  passengerFullName: string;
  onUpdateDocuments?: (documents: PassportDataWithName) => void;
  isLastRow?: boolean;
  onDeleteDocuments?: () => void;
}

export const TableRow = ({
  document,
  fieldPrefix,
  onUpdateDocuments,
  tableRowClassName,
  passengerFullName,
  isLastRow = false,
  onDeleteDocuments,
}: TableRowProps) => {
  const { isOpen, open, close } = useModalState();
  const {
    isOpen: isConfirmationDeleteFileModalOpen,
    open: openConfirmationDeleteFileModal,
    close: closeConfirmationDeleteFileModal,
  } = useModalState();

  const handleOpenDocumentModal = () => {
    open();
  };
  const handleCloseDocumentModal = () => {
    close();
  };

  const handleOpenConfirmationDeleteFileModal = () => {
    openConfirmationDeleteFileModal();
  };

  const handleUpdateDocuments = (documents: PassportDataWithName) => {
    onUpdateDocuments?.(documents);
    handleCloseDocumentModal();
  };

  return (
    <>
      <tr className={cn('h-[60px] bg-blue-50', tableRowClassName)}>
        <DocumentCell className="rounded-l-[12px] w-[40px]">
          <PreviewDocument document={document} />
        </DocumentCell>
        <DocumentCell>{passengerFullName}</DocumentCell>
        <DocumentCell>{document.document_name}</DocumentCell>
        <DocumentCell className="text-right">{document.passport_number}</DocumentCell>
        <DocumentCell className="text-right cursor-pointer">
          <Tooltip content={<TooltipWrapper>Date of Issue</TooltipWrapper>}>{document.issue_date}</Tooltip>
        </DocumentCell>
        <DocumentCell className="text-right cursor-pointer">
          <Tooltip content={<TooltipWrapper>Date of Expiration</TooltipWrapper>}>{document.expiry_date}</Tooltip>
        </DocumentCell>
        <DocumentCell className="rounded-r-[12px] text-right pr-[16px]">
          <button onClick={handleOpenDocumentModal} type="button" className="cursor-pointer p-[10px]">
            <EditIcon className="text-blue-300 w-[24px] h-[24px]" />
          </button>
          <button
            onClick={handleOpenConfirmationDeleteFileModal}
            color="danger"
            type="button"
            className="cursor-pointer p-[10px]"
          >
            <TrashIcon className="text-red-500 w-[24px] h-[24px]" />
          </button>
        </DocumentCell>
      </tr>
      {!isLastRow && (
        <tr className="h-[1px] bg-blue-100">
          <td colSpan={7} />
        </tr>
      )}
      {isConfirmationDeleteFileModalOpen ? (
        <ConfirmationDeleteFileModal
          onDelete={onDeleteDocuments}
          isOpen={isConfirmationDeleteFileModalOpen}
          onClose={closeConfirmationDeleteFileModal}
        />
      ) : null}
      {isOpen ? (
        <ViewDocumentModal
          onUpdateDocuments={handleUpdateDocuments}
          onDelete={handleOpenConfirmationDeleteFileModal}
          isOpen={isOpen}
          document={document}
          fieldPrefix={fieldPrefix}
          onClose={handleCloseDocumentModal}
        />
      ) : null}
    </>
  );
};

const DocumentCell = ({ children, className }: { children: React.ReactNode; className?: string }) => {
  return <td className={clsx('text-body-small text-black py-2 pl-4 align-middle ', className)}>{children}</td>;
};